import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ShareMetadata, ShareRecipient, DownloadAllResponse, SingleFileDownloadResponse, MultipleFilesDownloadResponse } from '@df-gate-share/shared';
import { apiClient, getServerErrorMessage } from '../lib/api';
import Header from '../components/Header';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

const ShareDetail: React.FC = () => {
  const { shareId } = useParams<{ shareId: string }>();
  const navigate = useNavigate();
  const [share, setShare] = useState<(ShareMetadata & { recipientDetails?: ShareRecipient[] }) | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [showRevokeDialog, setShowRevokeDialog] = useState(false);
  const [showExpiryDialog, setShowExpiryDialog] = useState(false);
  const [expiryPreset, setExpiryPreset] = useState<string>('');
  const [customExpiryDate, setCustomExpiryDate] = useState<string>('');
  const [expiryDialogError, setExpiryDialogError] = useState<string>('');
  // CR-045: 確定系操作の二重送信ガード
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchShareDetail();
  }, [shareId]);

  const fetchShareDetail = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await apiClient.get(`/api/v1/shares/${shareId}`);
      setShare(response.data.share);
    } catch (err) {
      setError('共有の詳細情報を取得できませんでした');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadAll = async () => {
    if (!share) return;

    try {
      const response = await apiClient.get(`/api/v1/shares/${shareId}/download-all`);
      const data = response.data as DownloadAllResponse;

      if (data.fileCount === 1) {
        // Single file - direct download
        window.open((data as SingleFileDownloadResponse).downloadUrl, '_blank');
      } else {
        // Multiple files - create zip archive
        const multiData = data as MultipleFilesDownloadResponse;
        if (multiData.files.length === 0) {
          setError('ダウンロード可能なファイルがありません');
          setTimeout(() => setError(''), 3000);
          return;
        }

        // Create zip archive
        const zip = new JSZip();

        // Download all files and add to zip
        for (const file of multiData.files) {
          try {
            const fileResponse = await fetch(file.downloadUrl);
            if (!fileResponse.ok) {
              throw new Error(`Failed to download ${file.fileName}`);
            }
            const blob = await fileResponse.blob();
            zip.file(file.fileName, blob);
          } catch (err) {
            console.error(`Error downloading ${file.fileName}:`, err);
            setError(`${file.fileName} のダウンロードに失敗しました`);
            setTimeout(() => setError(''), 3000);
            return;
          }
        }

        // Generate zip file and trigger download
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const zipFileName = share.title ? `${share.title}.zip` : 'files.zip';
        saveAs(zipBlob, zipFileName);
      }

      setSuccessMessage('ダウンロードを開始しました');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('ダウンロードに失敗しました');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDownloadFile = async (fileId: string, fileName: string) => {
    try {
      const response = await apiClient.get(`/api/v1/shares/${shareId}/download/${fileId}`);
      const downloadUrl = response.data.downloadUrl;
      window.open(downloadUrl, '_blank');

      setSuccessMessage(`${fileName} のダウンロードを開始しました`);
      setTimeout(() => setSuccessMessage(''), 3000);

      // Refresh share details to update download count
      fetchShareDetail();
    } catch (err) {
      setError(`${fileName} のダウンロードに失敗しました`);
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleUpdateExpiry = async () => {
    if (!share) return;

    setExpiryDialogError('');
    let newExpiresAt: string;

    if (expiryPreset === 'custom') {
      if (!customExpiryDate) {
        setExpiryDialogError('カスタム日時を入力してください');
        return;
      }
      const customDate = new Date(customExpiryDate);
      if (customDate <= new Date()) {
        setExpiryDialogError('有効期限は現在より未来の日時を指定してください');
        return;
      }
      // バックエンドと同じ基準: 共有作成日から30日以内
      const maxDate = new Date(share.createdAt);
      maxDate.setDate(maxDate.getDate() + 30);
      if (customDate > maxDate) {
        setExpiryDialogError('有効期限は共有作成日時から30日以内である必要があります');
        return;
      }
      newExpiresAt = customDate.toISOString();
    } else if (expiryPreset) {
      const days = parseInt(expiryPreset, 10);
      const newDate = new Date();
      newDate.setDate(newDate.getDate() + days);
      newExpiresAt = newDate.toISOString();
    } else {
      setExpiryDialogError('有効期限を選択してください');
      return;
    }

    if (isSubmitting) return;
    try {
      setIsSubmitting(true);
      await apiClient.put(`/api/v1/shares/${shareId}/expiry`, {
        expiresAt: newExpiresAt
      });

      setSuccessMessage('有効期限を更新しました');
      setShowExpiryDialog(false);
      setExpiryPreset('');
      setCustomExpiryDate('');
      setExpiryDialogError('');

      // Refresh share details
      fetchShareDetail();

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setExpiryDialogError(getServerErrorMessage(err, '有効期限の更新に失敗しました'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRevoke = async () => {
    if (!share || isSubmitting) return;

    try {
      setIsSubmitting(true);
      await apiClient.put(`/api/v1/shares/${shareId}/revoke`);
      setSuccessMessage('共有を無効化しました');
      setShowRevokeDialog(false);
      setTimeout(() => {
        navigate('/files');
      }, 1500);
    } catch (err) {
      setError('共有の無効化に失敗しました');
      setTimeout(() => setError(''), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };


  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateDaysRemaining = (expiresAt: string): { days: number; text: string; isDanger: boolean } => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diffTime = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return { days: diffDays, text: '期限切れ', isDanger: true };
    } else if (diffDays <= 3) {
      return { days: diffDays, text: `あと${diffDays}日で期限切れになります`, isDanger: true };
    } else {
      return { days: diffDays, text: `あと${diffDays}日`, isDanger: false };
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="page-wrapper text-center text-muted" style={{ padding: '3rem' }}>
          読み込み中...
        </div>
      </>
    );
  }

  if (!share) {
    return (
      <>
        <Header />
        <div className="page-wrapper text-center text-muted" style={{ padding: '3rem' }}>
          共有が見つかりません
        </div>
      </>
    );
  }

  const expiryInfo = calculateDaysRemaining(share.expiresAt);
  const isInactive = share.status === 'expired' || share.status === 'revoked';

  return (
    <>
      <Header />
      <div className="page-wrapper">
        <div className="page-container">
          {/* Back Link and Title */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/files')}
              className="btn btn--ghost btn--sm mb-3"
            >
              ← 送信履歴に戻る
            </button>
            <h1 className="page-title">送信詳細</h1>
          </div>

          {/* Success/Error Messages */}
          {error && (
            <div className="alert alert--error">
              {error}
            </div>
          )}

          {successMessage && (
            <div className="alert alert--success">
              {successMessage}
            </div>
          )}

          {/* Content Grid */}
          <div className="grid-detail" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '30px' }}>
            {/* Left Column */}
            <div>
              {/* Share Information Card */}
              <div className="card mb-8">
                <div className="card__body">
                  <h2 className="font-display font-semibold mb-6"><span className="material-symbols-outlined icon-sm">upload</span> 送信情報</h2>

                  <div className="flex flex-col gap-6">
                    <div>
                      <div className="section-label">タイトル</div>
                      <div className="font-semibold" style={{ fontSize: 'var(--text-lg)' }}>
                        {share.title}
                      </div>
                    </div>

                    <div>
                      <div className="section-label">送信日時</div>
                      <div className="font-medium">
                        {formatDate(share.createdAt)}
                      </div>
                    </div>

                    <div>
                      <div className="section-label">ステータス</div>
                      <span className={`badge ${
                        share.status === 'active' ? 'badge--active' :
                        share.status === 'expired' ? 'badge--expired' : 'badge--revoked'
                      }`}>
                        {share.status === 'active' ? 'アクティブ' :
                         share.status === 'expired' ? '期限切れ' : '無効化済み'}
                      </span>
                    </div>

                    <div>
                      <div className="section-label">有効期限</div>
                      <div className="font-medium">
                        {formatDate(share.expiresAt)}
                      </div>
                      {share.status === 'active' && (
                        <div className={`info-box ${expiryInfo.isDanger ? 'info-box--danger' : 'info-box--warning'} mt-4`}>
                          <span className="material-symbols-outlined icon-sm">schedule</span> {expiryInfo.text}
                        </div>
                      )}
                    </div>

                    <div>
                      <div className="section-label">受信者</div>
                      <div className="flex flex-col gap-2">
                        {share.recipients.map((recipient, index) => (
                          <div key={index} className="file-item">
                            <div className="file-item__info">
                              <span className="material-symbols-outlined icon-sm">mail</span> {recipient}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {share.message && (
                      <div>
                        <div className="section-label">メッセージ</div>
                        <div className="card" style={{ whiteSpace: 'pre-wrap', background: 'var(--color-surface-hover)' }}>
                          <div className="card__body text-sm">
                            {share.message}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Files List Card */}
              <div className="card mb-8">
                <div className="card__body">
                  <h2 className="font-display font-semibold mb-6"><span className="material-symbols-outlined icon-sm">attach_file</span> 送信ファイル一覧</h2>

                  <div className="flex flex-col gap-3 mb-4">
                    {share.fileNames && share.fileNames.length > 0 ? (
                      share.fileNames.map((fileName, index) => (
                        <div key={index} className="file-item">
                          <div className="file-item__info">
                            <span className="file-item__icon"><span className="material-symbols-outlined">description</span></span>
                            <div className="flex-1">
                              <div className="file-item__name">{fileName}</div>
                            </div>
                          </div>
                          {!isInactive && (
                            <button
                              onClick={() => handleDownloadFile(share.fileIds[index], fileName)}
                              className="btn btn--primary btn--sm"
                            >
                              <span className="material-symbols-outlined icon-sm">download</span> ダウンロード
                            </button>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="file-item text-muted text-sm">
                        {share.fileCount}個のファイル
                      </div>
                    )}
                  </div>

                  <div className="font-semibold text-right" style={{ paddingTop: 'var(--space-3)', borderTop: '1px solid var(--color-border-light)' }}>
                    合計: {formatFileSize(share.totalSize)} ({share.fileCount}ファイル)
                  </div>
                </div>
              </div>

              {/* Expiry Settings Card */}
              {!isInactive && (
                <div className="card mb-8">
                  <div className="card__body">
                    <h2 className="font-display font-semibold mb-6"><span className="material-symbols-outlined icon-sm">schedule</span> 有効期限の変更</h2>

                    <button
                      onClick={() => setShowExpiryDialog(true)}
                      className="btn btn--warning btn--block"
                    >
                      有効期限を変更
                    </button>
                  </div>
                </div>
              )}

              {/* Danger Zone */}
              {!isInactive && (
                <div className="danger-zone">
                  <div className="danger-zone__title"><span className="material-symbols-outlined icon-sm">warning</span> 危険な操作</div>
                  <div className="danger-zone__desc">
                    この共有リンクを無効化すると、すべての受信者はファイルにアクセスできなくなります。この操作は元に戻せません。
                  </div>
                  <button
                    onClick={() => setShowRevokeDialog(true)}
                    className="btn btn--danger"
                  >
                    共有を無効化
                  </button>
                </div>
              )}
            </div>

            {/* Right Column */}
            <div>
              {/* Quick Actions Card */}
              {!isInactive && (
                <div className="card mb-8">
                  <div className="card__body">
                    <h2 className="font-display font-semibold mb-6"><span className="material-symbols-outlined icon-sm">bolt</span> クイックアクション</h2>

                    <div className="flex flex-col gap-3">
                      <button
                        onClick={handleDownloadAll}
                        className="btn btn--primary btn--block"
                      >
                        <span className="material-symbols-outlined icon-sm">download</span> 全ファイルをダウンロード
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Recipient Status Card */}
              <div className="card">
                <div className="card__body">
                  <h2 className="font-display font-semibold mb-6"><span className="material-symbols-outlined icon-sm">group</span> 受信者別ステータス</h2>

                  <div className="flex flex-col gap-4">
                    {share.recipientDetails && share.recipientDetails.length > 0 ? (
                      share.recipientDetails.map((recipient, index) => (
                        <div key={index} className="file-item" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 'var(--space-2)' }}>
                          <div className="flex items-center gap-2">
                            <span className="material-symbols-outlined icon-sm">mail</span>
                            <span className="font-medium text-sm" style={{ wordBreak: 'break-all' }}>{recipient.recipientEmail}</span>
                          </div>
                          <div className="flex flex-col gap-1" style={{ paddingLeft: 'var(--space-6)' }}>
                            <div className="flex items-center gap-2">
                              <span className={`badge ${recipient.otpVerifiedAt ? 'badge--success' : 'badge--warning'}`}>
                                {recipient.otpVerifiedAt ? '確認済み' : '未確認'}
                              </span>
                              <span className="text-muted text-xs">
                                DL: {recipient.downloadCount || 0}回
                              </span>
                            </div>
                            {recipient.lastDownloadAt && (
                              <div className="text-muted text-xs">
                                最終DL: {formatDate(recipient.lastDownloadAt)}
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-muted text-sm">受信者情報がありません</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Download Stats Card */}
              <div className="card mt-8">
                <div className="card__body">
                  <h2 className="font-display font-semibold mb-6"><span className="material-symbols-outlined icon-sm">bar_chart</span> ダウンロード統計</h2>

                  <div className="flex flex-col gap-3">
                    <div className="summary-row">
                      <span className="summary-row__label">総ダウンロード数</span>
                      <span className="summary-row__value">
                        {share.downloadCount || 0}回
                      </span>
                    </div>

                    {share.lastDownloadAt && (
                      <div className="summary-row">
                        <span className="summary-row__label">最終ダウンロード日時</span>
                        <span className="summary-row__value">
                          {formatDate(share.lastDownloadAt)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Expiry Update Dialog */}
      {showExpiryDialog && (
        <div className="modal-overlay">
          <div className="modal">
            <h2 className="modal__title" style={{ color: 'var(--color-warning)' }}>
              <span className="material-symbols-outlined icon-sm">schedule</span> 有効期限の変更
            </h2>
            <p className="text-muted text-sm mb-6">
              共有「{share?.title}」の有効期限を変更します。
            </p>

            {expiryDialogError && (
              <div className="alert alert--error mb-3">
                {expiryDialogError}
              </div>
            )}

            <div className="form-group">
              <label className="form-label">新しい有効期限</label>
              <select
                value={expiryPreset}
                onChange={(e) => {
                  setExpiryPreset(e.target.value);
                  setExpiryDialogError('');
                }}
                className="form-select mb-3"
              >
                <option value="">プリセットから選択</option>
                <option value="1">1日後</option>
                <option value="7">7日後</option>
                <option value="30">30日後</option>
                <option value="custom">カスタム日時を指定</option>
              </select>

              {expiryPreset === 'custom' && (
                <div>
                  <label className="form-label">カスタム日時</label>
                  <input
                    type="datetime-local"
                    value={customExpiryDate}
                    onChange={(e) => {
                      setCustomExpiryDate(e.target.value);
                      setExpiryDialogError('');
                    }}
                    min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                    max={share?.createdAt ? new Date(new Date(share.createdAt).getTime() + 30 * 86400000).toISOString().slice(0, 16) : undefined}
                    className="form-input"
                  />
                </div>
              )}
            </div>

            <div className="modal__actions">
              <button
                onClick={() => {
                  setShowExpiryDialog(false);
                  setExpiryPreset('');
                  setCustomExpiryDate('');
                  setExpiryDialogError('');
                }}
                className="btn btn--secondary"
              >
                キャンセル
              </button>
              <button
                onClick={handleUpdateExpiry}
                disabled={isSubmitting}
                className="btn btn--warning"
              >
                {isSubmitting ? '変更中...' : '変更する'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revoke Confirmation Dialog */}
      {showRevokeDialog && (
        <div className="modal-overlay">
          <div className="modal">
            <h2 className="modal__title" style={{ color: 'var(--color-danger)' }}>
              <span className="material-symbols-outlined icon-sm">warning</span> 共有を無効化
            </h2>
            <p className="text-muted text-sm mb-6">
              共有「{share.title}」を無効化します。<br />
              すべての受信者はファイルにアクセスできなくなります。<br />
              この操作は取り消せません。
            </p>

            <div className="modal__actions">
              <button
                onClick={() => setShowRevokeDialog(false)}
                className="btn btn--secondary"
              >
                キャンセル
              </button>
              <button
                onClick={handleRevoke}
                disabled={isSubmitting}
                className="btn btn--danger"
              >
                {isSubmitting ? '無効化中...' : '無効化する'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShareDetail;
